<template>
    <Teleport to="#dojo-web-pos-payment-method-vue-app">
        <div v-if="errorMessage"
             class="alert alert-danger">
            <p>{{ errorMessage }}</p>
        </div>

        <div v-show="ready"
             class="payment-terminal-screen">
            <div v-if="!currentTransaction"
                 class="message-line">
                <p>{{ l('DOJO_WEBPOS.READY') }}</p>
            </div>
            <template v-if="currentTransaction">
                <div v-if="currentTransaction.amountMajorUnits"
                     class="message-line">
                    <span v-if="currentTransaction.amountMajorUnits">
                        {{ l('PAYMENT.TXN_AMOUNT') }} {{ currencyFilter(currentTransaction.amountMajorUnits) }}
                    </span>
                </div>

                <div v-if="currentTransaction.transactionResult.message"
                     :class="resultClass"
                     class="message-line">
                    <p><b>{{ currentTransaction.transactionResult.message }}</b></p>
                </div>
            </template>

            <div v-if="showStartRestartButton() === StartRestartButtonState.START"
                 class="dojo-action-btn">
                <button class="btn btn-block btn-success"
                        data-testid="dojo-start-btn"
                        :disabled="isTransactionInProgress"
                        @click="tryAgain()">
                    {{ l('PAYMENT.START_CARD_TRANSACTION_BTN', {amount: getRemainingBalance()}) }}
                </button>
            </div>

            <div v-if="showStartRestartButton() === StartRestartButtonState.RESTART"
                 class="dojo-action-btn">
                <button class="btn btn-block btn-primary"
                        :disabled="isTransactionInProgress"
                        @click="tryAgain()">
                    <i class="fa-solid fa-refresh" />
                    {{ l('PAYMENT.TRY_AGAIN', {amount: getRemainingBalance()}) }}
                </button>
            </div>
        </div>

        <template v-if="ready">
            <div class="btn-row btn-row--no-stretch">
                <button class="btn btn-default"
                        data-testid="dojo-partial-payment-btn"
                        :disabled="isPartialPaymentButtonDisabled()"
                        @click="makePartialPayment()">
                    <i class="fa-solid fa-arrow-up-right-and-arrow-down-left-from-center" />
                    {{ l('PAYMENT.PARTIAL_PAYMENT_BTN_DOJO') }}
                </button>
            </div>
        </template>
    </Teleport>
</template>

<script>
import DojoWebPosResult from '@/lib/PaymentMethods/Dojo/Enums/DojoWebPosResult';
import DojoWebPosTransaction from '@/lib/PaymentMethods/Dojo/DojoWebPosTransaction';
import EventNames from '@/lib/Events/EventNames';
import Payment from '@/lib/Payments/Payment';
import PaymentMethodRef from '@/lib/PaymentMethods/PaymentMethodRef';
import StartRestartButtonState from '@/lib/PaymentMethods/StartRestartButtonState';
import { container } from '@/container';
import { MODAL_NAMES__EPOS } from '@/epos/vue/modals/MODAL_NAMES__EPOS';
import { suppressModalCancelledErrorOrReThrow } from '@/lib/Errors';
import { timeoutPromise } from '@/functions/functions';
import { updateAngularUi } from '@/functions/angular';

export default {
    name: 'DojoWebPosPaymentMethod',

    data() {
        return {
            autoStartCardTransactions: null,

            ready: false,

            /**
             * @type {DojoWebPosTransaction|null}
             */
            currentTransaction: null,

            showTransactionResult: false,

            errorMessage: null,

            /**
             * @type {string}
             */
            currentStartRestartButtonState: StartRestartButtonState.NO_BUTTON,
        };
    },

    computed: {
        isTransactionInProgress() {
            return this.currentTransaction && !this.currentTransaction.isFinished;
        },
        resultClass() {
            if (this.currentTransaction.transactionResult.status === DojoWebPosResult.SUCCESS) {
                return 'message-line-success';
            }
            return 'message-line-danger';
        },
    },

    created() {
        this.paymentMethod = container.paymentMethodService.getByRef(PaymentMethodRef.CARD_DOJO_CHIP_AND_PIN);
        this.autoStartCardTransactions = container.currentDevice.device.autoStartCardTransactions;

        // Make the import available in the template
        this.StartRestartButtonState = StartRestartButtonState;

        this.init();
    },

    methods: {
        init() {
            console.log('Switched to DojoWebPos');

            try {
                this.ready = true;
                if (typeof WebPOS === 'undefined') {
                    throw new Error('DojoWebPos can only be used with the DojoWebPos Android application or the DojoWebPosSimulator');
                }
            } catch (error) {
                this.ready = false;
                container.paymentState.clearPaymentModalCancelable();
                this.setError(error);
                return;
            }

            // Check if there are abandoned transactions we need to resume.
            // This happens (for example) if the browser was refreshed while at the payment stage.

            const transactionInProgress = container.cartService.getTransactionInProgress();

            if (
                transactionInProgress
                && transactionInProgress.paymentMethodRef === PaymentMethodRef.CARD_DOJO_CHIP_AND_PIN
            ) {
                // Given the nature of integration, this may not be a possible scenario but, we can think about what
                // we can do which is to create a new transaction using the existing amount + txnRef.
                console.log('There are transactions to recover');

                this.recoverTransaction(
                    transactionInProgress.txnRef,
                    transactionInProgress.amount
                );
            } else {
                if (this.autoStartCardTransactions) {
                    // Tell the driver to initiate the transaction.
                    console.log('Switched to DojoWebPos NFC Payment method. Start new transaction');
                    this.initiateTransaction(this.getRemainingBalance());
                } else {
                    // The operator should start the transaction manually.
                }
            }
        },

        getRemainingBalance() {
            // This won't work as a computed property but, it is ok because this does not get called unnecessarily.
            return container.cartService.getRemainingBalance();
        },

        setError(error) {
            this.errorMessage = error.message;
        },

        clearError() {
            this.errorMessage = null;
        },

        initiateTransaction(amount) {
            try {
                console.log('DojoWebPos initiateTransaction...', amount);

                if (!this.ready) {
                    throw new Error('Can\'t start DojoWebPos transaction because config is not valid.');
                }

                let dojoWebPosTransaction = new DojoWebPosTransaction(amount);
                dojoWebPosTransaction.thirdPartyID = container.cartService.generatePaymentThirdPartyID();

                this.currentTransaction = dojoWebPosTransaction;

                if (this.currentTransaction.isSale()) {
                    this.setSaleResponseHandler();
                    // eslint-disable-next-line no-undef
                    WebPOS.INIT_SALE(this.currentTransaction.amountMinorUnits);
                } else {
                    this.setRefundResponseHandler();
                    // eslint-disable-next-line no-undef
                    WebPOS.INIT_REFUND(this.currentTransaction.amountMinorUnits);
                }
            } catch (error) {
                this.setError(error);
            }
        },

        recoverTransaction(txnRef, amount) {
            try {
                console.log('DojoWebPos recoverTransaction...', txnRef, amount);

                if (!this.ready) {
                    throw new Error('Can\'t start DojoWebPos transaction because config is not valid.');
                }

                let dojoWebPosTransaction = new DojoWebPosTransaction(amount);
                dojoWebPosTransaction.thirdPartyID = txnRef;

                this.currentTransaction = dojoWebPosTransaction;

                if (this.currentTransaction.isSale()) {
                    this.setSaleResponseHandler();
                    // eslint-disable-next-line no-undef
                    WebPOS.INIT_SALE(this.currentTransaction.amountMinorUnits);
                } else {
                    this.setRefundResponseHandler();
                    // eslint-disable-next-line no-undef
                    WebPOS.INIT_REFUND(this.currentTransaction.amountMinorUnits);
                }
            } catch (error) {
                this.setError(error);
            }
        },

        setSaleResponseHandler() {
            window.saleOutcome = (outcome, baseAmount, gratuity) => {
                delete window.saleOutcome;

                this.currentTransaction.setTransactionResult(outcome);
                this.currentTransaction.setResponseAmount(baseAmount);
                this.currentTransaction.addGratuity(gratuity);

                if (outcome === DojoWebPosResult.SUCCESS) {
                    this.handleApprovedResponse();
                } else {
                    this.handleFailureResponse();
                }
            };
        },

        setRefundResponseHandler() {
            window.refundOutcome = (outcome) => {
                delete window.refundOutcome;

                this.currentTransaction.setTransactionResult(outcome);
                if (outcome === DojoWebPosResult.SUCCESS) {
                    this.handleApprovedResponse();
                } else {
                    this.handleFailureResponse();
                }
            };
        },

        makePartialPayment() {
            container.vueModalFactory
                .open(
                    MODAL_NAMES__EPOS.PARTIAL_PAYMENT,
                    {
                        mode: 'make',
                        isRefund: container.cartService.isRefund(),
                        paymentMethod: container.cartService.getSelectedPaymentMethod(),
                        orderTotal: container.cartService.getTotalExcludingOriginalOrder(),
                        remainingBalance: container.cartService.getRemainingBalance(),
                        maxPayment: container.cartService.getRemainingMaxPayment(),
                    }
                )
                .then((result) => {
                    this.initiateTransaction(result.value);
                })
                .catch(suppressModalCancelledErrorOrReThrow);
        },

        isPartialPaymentButtonDisabled() {
            // As soon as the Start button is clicked we need to disable the button.
            if (this.isTransactionInProgress) {
                return true;
            }
            return this.currentStartRestartButtonState === StartRestartButtonState.NO_BUTTON;
        },

        /**
         * Should we show the start button, the restart button, or no button at all?
         *
         * @return {string}
         */
        showStartRestartButton() {
            const setState = (state) => {
                this.currentStartRestartButtonState = state;
                return state;
            };

            // This state only applies when using the simulator in the browser because when running in the WebPOS
            // ProPoint will be hidden at this pint.
            if (this.isTransactionInProgress) {
                return setState(StartRestartButtonState.NO_BUTTON);
            }

            // There has been an error or the user has cancelled the payment in order to make a partial payment.
            if (this.currentTransaction && this.currentTransaction.hasError) {
                return setState(StartRestartButtonState.RESTART);
            }

            // The transaction is finished don't show any buttons while we show this result to the user.
            if (this.currentTransaction && this.currentTransaction.isFinished && this.showTransactionResult) {
                return setState(StartRestartButtonState.NO_BUTTON);
            }

            // There is an outstanding balance for example after a partial payment.
            if (this.currentTransaction && this.currentTransaction.isFinished && this.getRemainingBalance() > 0) {
                return setState(StartRestartButtonState.START);
            }

            // There is no transaction in progress this.autoStartCardTransactions === false.
            if (!this.currentTransaction) {
                return setState(StartRestartButtonState.START);
            }

            return setState(StartRestartButtonState.NO_BUTTON);
        },

        handleFailureResponse() {
            this.currentTransaction.isFinished = true;
            this.currentTransaction.hasError = true;
        },

        tryAgain() {
            this.clearError();
            this.initiateTransaction(this.getRemainingBalance());
        },

        async handleApprovedResponse() {
            this.currentTransaction.isFinished = true;

            // Funds received. Add Payment to cart/order.
            let payment = new Payment();

            const amount = this.currentTransaction.amountMajorUnits;
            payment.setTendered(amount);

            payment.setGratuity(this.currentTransaction.gratuityMajorUnits);
            payment.setPaymentMethod(this.paymentMethod);

            payment.setStatusDetail(this.currentTransaction.result);
            payment.debugInfo = null;

            payment.setThirdPartyID(this.currentTransaction.thirdPartyID);

            await this.addPayment(payment);
        },

        async addPayment(payment) {
            try {
                // Timeout so user can see the status of the payment.
                // Set a variable to control button state.
                this.showTransactionResult = true;
                await timeoutPromise(2000);
                this.showTransactionResult = false;

                container.cartService.clearTransactionInProgress();

                // Add the payment to the cart.
                container.eventBus.broadcast(EventNames.ADD_PAYMENT_TO_CART_REQUESTED, payment);
                updateAngularUi();
            } catch (error) {
                // So we won't proceed. Just show an error.
                this.setError(error);
            } finally {
                // Anything we need to do to tidy up goes here.
                container.paymentState.setPaymentModalCancelable(true, this.paymentMethod.ref);
            }
        },
    },
};
</script>
